import { useEffect, useRef, useState } from 'react';
import {
  appendStagedProductImages,
  removeStagedProductImage,
  revokeStagedProductImages,
  type StagedProductImage,
} from '../lib/stagedProductImages';

export interface CreateProductImagesProps {
  images: StagedProductImage[];
  primaryKey: string | null;
  onImagesChange(images: StagedProductImage[]): void;
  onPrimaryChange(key: string | null): void;
  disabled?: boolean;
}

export function CreateProductImages({
  images,
  primaryKey,
  onImagesChange,
  onPrimaryChange,
  disabled = false,
}: CreateProductImagesProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const latestImages = useRef(images);
  const [error, setError] = useState<string | null>(null);
  latestImages.current = images;

  useEffect(
    () => () => revokeStagedProductImages(latestImages.current),
    [],
  );

  const addFiles = (files: FileList | null): void => {
    if (!files?.length) return;
    setError(null);
    try {
      const next = appendStagedProductImages(images, files);
      onImagesChange(next);
      if (!primaryKey && next[0]) onPrimaryChange(next[0].key);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Images could not be selected.',
      );
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (key: string): void => {
    const next = removeStagedProductImage(images, key);
    onImagesChange(next);
    if (primaryKey === key) onPrimaryChange(next[0]?.key ?? null);
  };

  return (
    <section
      className="stack product-form__section create-product-images"
      aria-labelledby="product-images-heading"
    >
      <div className="spread create-product-images__heading">
        <h2 id="product-images-heading" className="product-form__section-title">
          Product Images
        </h2>
        <button
          type="button"
          className="btn"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          + Add Images
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="visually-hidden"
        aria-label="Add product images"
        onChange={(event) => addFiles(event.target.files)}
      />
      <p className="hint mt-0">
        JPEG, PNG, or WebP. Images are saved only after the product is created.
      </p>

      {images.length > 0 ? (
        <div className="create-product-images__grid">
          {images.map((image, index) => {
            const isPrimary = image.key === primaryKey;
            return (
              <article className="create-product-image" key={image.key}>
                <img
                  src={image.previewUrl}
                  alt={`Selected product image ${index + 1}`}
                />
                <div className="create-product-image__actions">
                  <label className="create-product-image__primary">
                    <input
                      type="radio"
                      name="staged-primary-image"
                      checked={isPrimary}
                      onChange={() => onPrimaryChange(image.key)}
                      disabled={disabled}
                    />
                    {isPrimary ? 'Primary' : 'Make Primary'}
                  </label>
                  <button
                    type="button"
                    className="product-form__remove-optional"
                    onClick={() => remove(image.key)}
                    disabled={disabled}
                    aria-label={`Remove selected image ${index + 1}`}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {error ? (
        <div className="error-text" role="alert">
          {error}
        </div>
      ) : null}
    </section>
  );
}
